import { Table, type TableProps } from 'antd';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as permissionKind from '../../../../shared/permissions';
import { type RouterSpecsFragment } from '../../../api/fragments/RouterSpecs';
import hasPermissions from '../../../utilities/hasPermissions';
import renderBooleanIcon from '../../../utilities/renderBooleanIcon';
import ResponsiveStyledTable from '../../shared/ResponsiveStyledTable';
import EndpointActions from './EndpointActions';
import { getIsPrivate, getTitle, hasViewPanel, renderViewPanel } from './endpoints';

export type EndpointsTableProps = {
    router: RouterSpecsFragment;
};

type EndpointSpecs = RouterSpecsFragment['endpoints'][number];

const renderIsPrivate = (value: any, record: EndpointSpecs) => renderBooleanIcon(getIsPrivate(record), true);

const EndpointsTable = ({ router }: EndpointsTableProps) => {
    const { t } = useTranslation(['routerDetails']);

    const expandable = useMemo(
        (): TableProps<EndpointSpecs>['expandable'] => ({
            expandedRowRender: record => renderViewPanel(record, router),
            rowExpandable: record => hasViewPanel(record),
        }),
        [router]
    );

    const renderActions = useCallback(
        (value: any, record: EndpointSpecs) => <EndpointActions endpoint={record} router={router} />,
        [router]
    );

    const renderTypeName = useCallback((value: any, record: EndpointSpecs) => getTitle(record, t), [t]);

    return (
        <ResponsiveStyledTable
            dataSource={router.endpoints}
            expandable={expandable}
            pagination={false}
            rowKey="id"
            firstColumn
        >
            <Table.Column
                key="pathname"
                dataIndex="pathname"
                title={t('routerDetails:endpointTable.columns.pathname')}
            />
            <Table.Column
                key="private"
                align="center"
                render={renderIsPrivate}
                title={t('routerDetails:endpointTable.columns.private')}
                width={120}
            />
            <Table.Column
                key="typename"
                dataIndex="__typename"
                render={renderTypeName}
                title={t('routerDetails:endpointTable.columns.type')}
            />
            {hasPermissions(router.permissions, [permissionKind.updateRouter]) ? (
                <Table.Column
                    key="actions"
                    align="center"
                    render={renderActions}
                    title={t('routerDetails:endpointTable.columns.actions')}
                    width={100}
                />
            ) : null}
        </ResponsiveStyledTable>
    );
};

export default EndpointsTable;
